// wcwb/frontend/src/components/HostDashboardPanel.js
// Day-before summary for hosts

import { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const API = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';

const ALLERGEN_MAP = {
  'Vegetarian':   ['meat', 'chicken', 'beef', 'pork', 'lamb', 'fish', 'seafood', 'bacon', 'prosciutto'],
  'Vegan':        ['meat', 'chicken', 'beef', 'pork', 'lamb', 'fish', 'seafood', 'dairy', 'milk', 'cheese', 'butter', 'cream', 'egg', 'honey'],
  'Gluten-free':  ['flour', 'bread', 'pasta', 'wheat', 'barley', 'rye', 'gluten', 'soy sauce', 'breadcrumb'],
  'Dairy-free':   ['milk', 'cheese', 'butter', 'cream', 'yogurt', 'dairy', 'brie', 'feta', 'parmesan'],
  'Pescatarian':  ['chicken', 'beef', 'pork', 'lamb', 'meat', 'bacon'],
  'Nut allergy':  ['nut', 'almond', 'cashew', 'walnut', 'pecan', 'pistachio', 'hazelnut', 'peanut'],
  'Halal':        ['pork', 'bacon', 'ham', 'alcohol', 'wine', 'beer'],
  'Kosher':       ['pork', 'bacon', 'ham', 'shellfish', 'prawn', 'lobster', 'crab'],
};

function checkDietaryConflicts(dishes, guests) {
  const conflicts = [];
  const approvedDishes = dishes.filter(d => d.status === 'approved');

  for (const guest of guests) {
    const dietary = guest.profiles?.dietary || [];
    if (!dietary.length) continue;

    for (const restriction of dietary) {
      const allergens = ALLERGEN_MAP[restriction] || [];
      for (const dish of approvedDishes) {
        const searchText = `${dish.name} ${dish.description || ''} ${dish.recipe || ''}`.toLowerCase();
        const found = allergens.find(a => searchText.includes(a));
        if (found) {
          conflicts.push({
            guestName: guest.profiles?.full_name,
            restriction,
            dishName: dish.name,
            ingredient: found,
            dishGuestName: dish.profiles?.full_name,
          });
        }
      }
    }
  }
  return conflicts;
}

export default function HostDashboardPanel({ event }) {
  const { token } = useAuth();
  const [assignments, setAssignments] = useState([]);
  const [showAssignForm, setShowAssignForm] = useState(false);
  const [assignForm, setAssignForm] = useState({ guest_id:'', dish_name:'', message:'' });
  const [assigning, setAssigning] = useState(false);
  const [deadlineEdit, setDeadlineEdit] = useState(false);
  const [deadline, setDeadline] = useState(event.suggest_deadline || '');
  const [savingDeadline, setSavingDeadline] = useState(false);

  const guests = event.event_guests || [];
  const dishes = event.dishes || [];
  const confirmed = guests.filter(g => g.status !== 'declined');
  const conflicts = checkDietaryConflicts(dishes, guests);
  const tomorrow = new Date(); tomorrow.setDate(tomorrow.getDate() + 1);
  const isEventTomorrow = new Date(event.date).toDateString() === tomorrow.toDateString();
  const deadlinePassed = deadline && new Date(deadline) < new Date();

  useEffect(() => {
    fetchAssignments();
  }, [event.id]);

  async function fetchAssignments() {
    try {
      const res = await fetch(`${API}/assignments?event_id=${event.id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await res.json();
      if (Array.isArray(data)) setAssignments(data);
    } catch(e) { console.error(e); }
  }

  async function saveDeadline() {
    setSavingDeadline(true);
    try {
      await fetch(`${API}/events/${event.id}`, {
        method: 'PATCH',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ suggest_deadline: deadline || null }),
      });
      setDeadlineEdit(false);
    } catch(e) { console.error(e); }
    finally { setSavingDeadline(false); }
  }

  async function assignDish() {
    if (!assignForm.guest_id || !assignForm.dish_name) return;
    setAssigning(true);
    try {
      const res = await fetch(`${API}/assignments`, {
        method: 'POST',
        headers: { 'Content-Type':'application/json', Authorization:`Bearer ${token}` },
        body: JSON.stringify({ event_id: event.id, ...assignForm }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      setAssignments(prev => [data, ...prev]);
      setAssignForm({ guest_id:'', dish_name:'', message:'' });
      setShowAssignForm(false);
    } catch(e) { console.error(e); }
    finally { setAssigning(false); }
  }

  async function deleteAssignment(id) {
    await fetch(`${API}/assignments/${id}`, {
      method: 'DELETE',
      headers: { Authorization: `Bearer ${token}` }
    });
    setAssignments(prev => prev.filter(a => a.id !== id));
  }

  const inputStyle = { width:'100%', padding:'9px 12px', border:'1px solid #E5D9C8', borderRadius:9, fontFamily:'Jost,sans-serif', fontSize:13, color:'#2C1A0E', outline:'none', marginBottom:12, background:'white' };

  return (
    <div style={{ display:'flex', flexDirection:'column', gap:16 }}>

      {/* Day-before summary */}
      {isEventTomorrow && (
        <div style={{ background:'linear-gradient(135deg,#2C1A0E,#5A3E2B)', borderRadius:16, padding:24, color:'#FAF7F2' }}>
          <p style={{ fontSize:11, letterSpacing:'.1em', textTransform:'uppercase', opacity:.5, marginBottom:8 }}>🎉 Tomorrow's the night!</p>
          <h3 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:22, marginBottom:16 }}>{event.title} — Final Summary</h3>
          <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr 1fr', gap:12, marginBottom:16 }}>
            {[
              { label:'Guests attending', val: confirmed.length + 1 },
              { label:'Dishes confirmed', val: dishes.filter(d=>d.status==='approved').length },
              { label:'Dietary flags',    val: [...new Set(guests.flatMap(g=>g.profiles?.dietary||[]))].length },
            ].map(s => (
              <div key={s.label} style={{ background:'rgba(255,255,255,.08)', borderRadius:10, padding:12, textAlign:'center' }}>
                <div style={{ fontFamily:'Cormorant Garamond,serif', fontSize:26, fontWeight:600 }}>{s.val}</div>
                <div style={{ fontSize:11, opacity:.55, marginTop:2 }}>{s.label}</div>
              </div>
            ))}
          </div>
          {/* Who's coming */}
          <p style={{ fontSize:12, opacity:.5, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8 }}>Confirmed guests</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6, marginBottom:16 }}>
            {confirmed.map(g => (
              <span key={g.user_id} style={{ background:'rgba(255,255,255,.1)', padding:'4px 10px', borderRadius:12, fontSize:12 }}>
                {g.profiles?.full_name}
                {g.profiles?.dietary?.length ? ` (${g.profiles.dietary.join(', ')})` : ''}
              </span>
            ))}
          </div>
          {/* What's on the menu */}
          <p style={{ fontSize:12, opacity:.5, textTransform:'uppercase', letterSpacing:'.08em', marginBottom:8 }}>On the menu</p>
          <div style={{ display:'flex', flexWrap:'wrap', gap:6 }}>
            {dishes.filter(d=>d.status==='approved').map(d => (
              <span key={d.id} style={{ background:'rgba(196,97,42,.25)', border:'1px solid rgba(196,97,42,.4)', padding:'4px 10px', borderRadius:12, fontSize:12 }}>
                {d.name} <span style={{ opacity:.6 }}>by {d.profiles?.full_name}</span>
              </span>
            ))}
          </div>
        </div>
      )}

      {/* Dietary conflict warnings */}
      {conflicts.length > 0 && (
        <div style={{ background:'#FFF5F5', border:'2px solid #FFCDD2', borderRadius:14, padding:20 }}>
          <div style={{ display:'flex', alignItems:'center', gap:8, marginBottom:12 }}>
            <span style={{ fontSize:20 }}>⚠️</span>
            <h4 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, color:'#B71C1C' }}>Dietary conflict{conflicts.length > 1 ? 's' : ''} detected</h4>
          </div>
          {conflicts.map((c, i) => (
            <div key={i} style={{ background:'white', borderRadius:10, padding:'12px 14px', marginBottom:8, fontSize:13, borderLeft:'3px solid #B71C1C' }}>
              <p style={{ fontWeight:500, marginBottom:3 }}>
                <span style={{ color:'#B71C1C' }}>{c.guestName}</span> is <span style={{ color:'#B71C1C' }}>{c.restriction}</span>
              </p>
              <p style={{ color:'#7A6555' }}>
                "{c.dishName}" by {c.dishGuestName} may contain <strong>{c.ingredient}</strong> — worth checking with them!
              </p>
            </div>
          ))}
        </div>
      )}

      {/* Suggestion deadline */}
      <div style={{ background:'white', borderRadius:14, border:'1px solid #E5D9C8', padding:20 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:12 }}>
          <div>
            <h4 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, marginBottom:2 }}>Suggestion deadline</h4>
            <p style={{ fontSize:12, color:'#7A6555' }}>Guests cannot suggest dishes after this date</p>
          </div>
          <button onClick={() => setDeadlineEdit(!deadlineEdit)}
            style={{ background:'none', border:'none', cursor:'pointer', color:'#C4612A', fontSize:13, fontWeight:500 }}>
            {deadlineEdit ? 'Cancel' : deadline ? 'Change' : 'Set deadline'}
          </button>
        </div>

        {!deadlineEdit && deadline && (
          <div style={{ display:'flex', alignItems:'center', gap:8 }}>
            <span style={{ fontSize:13, padding:'4px 10px', borderRadius:8, background: deadlinePassed ? '#FFEBEE' : '#E8F5E9', color: deadlinePassed ? '#B71C1C' : '#1B5E20', fontWeight:500 }}>
              {deadlinePassed ? '🔒 Closed' : '⏰ Open until'} {new Date(deadline).toLocaleDateString('en-US',{month:'long',day:'numeric'})}
            </span>
          </div>
        )}
        {!deadlineEdit && !deadline && (
          <p style={{ fontSize:13, color:'#7A6555', fontStyle:'italic' }}>No deadline set — guests can suggest at any time</p>
        )}
        {deadlineEdit && (
          <div style={{ display:'flex', gap:8, alignItems:'center' }}>
            <input type="date" style={{ ...inputStyle, marginBottom:0, flex:1 }}
              value={deadline} onChange={e => setDeadline(e.target.value)}/>
            <button onClick={saveDeadline}
              style={{ background:'#C4612A', color:'white', border:'none', borderRadius:9, padding:'9px 16px', fontFamily:'Jost,sans-serif', fontSize:13, cursor:'pointer', opacity:savingDeadline?0.5:1 }}
              disabled={savingDeadline}>
              {savingDeadline ? 'Saving…' : 'Save'}
            </button>
            {deadline && (
              <button onClick={() => { setDeadline(''); }}
                style={{ background:'none', border:'1px solid #E5D9C8', borderRadius:9, padding:'9px 16px', fontFamily:'Jost,sans-serif', fontSize:13, cursor:'pointer', color:'#7A6555' }}>
                Remove
              </button>
            )}
          </div>
        )}
      </div>

      {/* Dish assignments */}
      <div style={{ background:'white', borderRadius:14, border:'1px solid #E5D9C8', padding:20 }}>
        <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:16 }}>
          <div>
            <h4 style={{ fontFamily:'Cormorant Garamond,serif', fontSize:18, marginBottom:2 }}>Assign dishes to guests</h4>
            <p style={{ fontSize:12, color:'#7A6555' }}>Request a specific dish from a specific guest</p>
          </div>
          <button onClick={() => setShowAssignForm(!showAssignForm)}
            style={{ background:'#C4612A', color:'white', border:'none', borderRadius:9, padding:'8px 14px', fontFamily:'Jost,sans-serif', fontSize:13, cursor:'pointer' }}>
            + Assign dish
          </button>
        </div>

        {showAssignForm && (
          <div style={{ background:'#FAF7F2', borderRadius:12, padding:16, marginBottom:16, border:'1px solid #E5D9C8' }}>
            <label style={{ display:'block', fontSize:11, fontWeight:500, letterSpacing:'.07em', textTransform:'uppercase', color:'#7A6555', marginBottom:6 }}>Guest</label>
            <select style={inputStyle} value={assignForm.guest_id} onChange={e => setAssignForm(f=>({...f,guest_id:e.target.value}))}>
              <option value="">Select a guest…</option>
              {confirmed.map(g => (
                <option key={g.user_id} value={g.user_id}>{g.profiles?.full_name}</option>
              ))}
            </select>
            <label style={{ display:'block', fontSize:11, fontWeight:500, letterSpacing:'.07em', textTransform:'uppercase', color:'#7A6555', marginBottom:6 }}>Dish to bring</label>
            <input style={inputStyle} placeholder="e.g. A green salad, Garlic bread…"
              value={assignForm.dish_name} onChange={e => setAssignForm(f=>({...f,dish_name:e.target.value}))}/>
            <label style={{ display:'block', fontSize:11, fontWeight:500, letterSpacing:'.07em', textTransform:'uppercase', color:'#7A6555', marginBottom:6 }}>Personal note (optional)</label>
            <input style={inputStyle} placeholder="e.g. You make the best caesar salad!"
              value={assignForm.message} onChange={e => setAssignForm(f=>({...f,message:e.target.value}))}/>
            <div style={{ display:'flex', gap:8 }}>
              <button style={{ background:'none', border:'1px solid #E5D9C8', borderRadius:9, padding:'8px 14px', fontFamily:'Jost,sans-serif', fontSize:13, cursor:'pointer' }}
                onClick={() => setShowAssignForm(false)}>Cancel</button>
              <button
                style={{ background:'#C4612A', color:'white', border:'none', borderRadius:9, padding:'8px 16px', fontFamily:'Jost,sans-serif', fontSize:13, cursor:'pointer', opacity:(!assignForm.guest_id||!assignForm.dish_name||assigning)?0.5:1 }}
                disabled={!assignForm.guest_id||!assignForm.dish_name||assigning}
                onClick={assignDish}>
                {assigning ? 'Sending…' : 'Send request'}
              </button>
            </div>
          </div>
        )}

        {assignments.length === 0 && !showAssignForm && (
          <p style={{ fontSize:13, color:'#7A6555', fontStyle:'italic' }}>No dish assignments yet.</p>
        )}

        {assignments.map(a => (
          <div key={a.id} style={{ display:'flex', alignItems:'center', gap:12, padding:'12px 14px', borderRadius:10, background:'#FAF7F2', border:'1px solid #E5D9C8', marginBottom:8 }}>
            <div style={{ flex:1 }}>
              <p style={{ fontWeight:500, fontSize:13 }}>{a.profiles?.full_name}</p>
              <p style={{ fontSize:12, color:'#7A6555' }}>Requested to bring: <strong>{a.dish_name}</strong></p>
              {a.message && <p style={{ fontSize:12, color:'#7A6555', fontStyle:'italic' }}>"{a.message}"</p>}
            </div>
            <span style={{ fontSize:11, padding:'3px 8px', borderRadius:8, fontWeight:500,
              background: a.status==='accepted'?'#E8F5E9':a.status==='declined'?'#FFEBEE':'#FFF3E0',
              color: a.status==='accepted'?'#1B5E20':a.status==='declined'?'#B71C1C':'#BF360C' }}>
              {a.status==='accepted'?'✓ Accepted':a.status==='declined'?'✕ Declined':'⏳ Pending'}
            </span>
            <button onClick={() => deleteAssignment(a.id)}
              style={{ background:'none', border:'none', cursor:'pointer', color:'#7A6555', fontSize:18, lineHeight:1 }}>×</button>
          </div>
        ))}
      </div>

    </div>
  );
}
